import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import logo from "../data/New-logo.png";
import styles from "./Header.module.css";
import "aos/dist/aos.css";


export const Navigation = () => {
  const [scrolling, setScrolling] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expertiseDropdownOpen, setExpertiseDropdownOpen] = useState(false);
  const location = useLocation();
  const [isMobileView, setIsMobileView] = useState(false); // For detecting mobile view

  // Refs to detect clicks outside
  const mobileMenuRef = useRef(null);
  const expertiseRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  useEffect(() => {
    // Only add scroll listener in browser
    if (typeof window === 'undefined') return;
    
    const handleScroll = () => {
      setScrolling(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Detect mobile view based on screen width - show hamburger menu below small laptop size
  useEffect(() => {
    // Only add resize listener in browser
    if (typeof window === 'undefined') return;
    
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 1365);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initial check on component mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // AOS is now initialized globally in App.jsx
  }, []);


  // Click outside logic
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close mobile menu if click is outside both the toggle button and the dropdown
      const clickedInsideToggle = mobileMenuRef.current && mobileMenuRef.current.contains(event.target);
      const clickedInsideDropdown = mobileDropdownRef.current && mobileDropdownRef.current.contains(event.target);
      if (!clickedInsideToggle && !clickedInsideDropdown) {
        setMobileMenuOpen(false);
      }
      if (expertiseRef.current && !expertiseRef.current.contains(event.target)) {
        setExpertiseDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prevState) => !prevState);
  };



  const handleNavLinkClick = () => {
    setMobileMenuOpen(false);
    setExpertiseDropdownOpen(false);
  };

  const navbarBackground =
    (scrolling || location.pathname !== "/")
      ? styles.navbarScrolled
      : "";

  // Mobile styles (inline)
  const mobileOverlayStyles = isMobileMenuOpen ? {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.8)", // grey overlay
    zIndex: "999", // Behind the dropdown links
    pointerEvents: "none", // Make sure it doesn't block the clicks
  } : {};

  const mobileDropdownStyles = isMobileMenuOpen ? {
    position: "fixed",
    top: "50px", // Below the header
    left: "0",
    width: "100%",
    background: "linear-gradient(to bottom, black, rgba(0, 0, 0, 0.5))",
    color: "white",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    zIndex: "1000", // Higher than the overlay
    display: "flex",
    flexDirection: "column",
    padding: "20px 0",
    pointerEvents: "auto", // Ensure links are clickable
  } : {};

  return (
    <nav id="menu" className={`${styles.navbar} ${navbarBackground}`}>
      <div className={styles.container}>
        <div className={styles.navbarHeader}>
          <div className={styles.logoContainer} data-aos="fade-up">
            <NavLink to="/" className={styles.logoLink} data-aos="fade-up">
              <img src={logo} alt="logo" className={styles.logo} />
            </NavLink>
            <a className={`${styles.navbarBrand} customNavbarBrand`} href="/" data-aos="fade-up">
              <span className={styles.companyNameWrapper}>
                <span className={styles.companyNameLine1}>Ace Keystone</span>
                <span className={styles.companyNameLine2}>Consultants</span>
              </span>
            </a>
          </div>
          <button
            type="button"
            className={`${styles.navbarToggle} ${isMobileMenuOpen ? styles.active : ""}`}
            onClick={toggleMobileMenu}
            ref={mobileMenuRef}
          >
            <span className={styles.iconBar}></span>
            <span className={styles.iconBar}></span>
            <span className={styles.iconBar}></span>
          </button>
        </div>

        {/* Desktop Navigation (Dropdown below hamburger icon) */}
        {!isMobileView && (
          <div className={`${styles.navbarCollapse} ${isMobileMenuOpen ? styles.show : ""}`} ref={mobileMenuRef} >
            <ul className={styles.navbarNav}>
              <li data-aos="fade-up">
                <NavLink 
                  to="/about" 
                  className={({ isActive }) => `${styles.pageScroll} ${isActive ? styles.active : ''}`}
                  onClick={handleNavLinkClick}
                >
                  About Us
                </NavLink>
              </li>
              <li data-aos="fade-up">
                <NavLink 
                  to="/portfolio" 
                  className={({ isActive }) => `${styles.pageScroll} ${isActive ? styles.active : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Portfolio
                </NavLink>
              </li>
              <li 
                data-aos="fade-up" 
                className={styles.dropdownContainer}
                ref={expertiseRef}
              >
                <span 
                  className={`${styles.pageScroll} ${styles.dropdownTrigger}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpertiseDropdownOpen(!expertiseDropdownOpen);
                  }}
                >
                  Expertise <i className={`fa fa-chevron-${expertiseDropdownOpen ? 'up' : 'down'}`} style={{ fontSize: '12px', marginLeft: '5px' }}></i>
                </span>
                {expertiseDropdownOpen && (
                  <div className={styles.dropdownMenu}>
                    <NavLink 
                      to="/ourtech" 
                      className={styles.dropdownItem}
                      onClick={handleNavLinkClick}
                    >
                      Our Tech
                    </NavLink>
                    <NavLink 
                      to="/services" 
                      className={styles.dropdownItem}
                      onClick={handleNavLinkClick}
                    >
                      Our Services
                    </NavLink>
                    <NavLink 
                      to="/our-products" 
                      className={styles.dropdownItem}
                      onClick={handleNavLinkClick}
                    >
                      Our Products
                    </NavLink>
                  </div>
                )}
              </li>
              <li data-aos="fade-up">
                <NavLink 
                  to="/teams" 
                  className={({ isActive }) => `${styles.pageScroll} ${isActive ? styles.active : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Our Team
                </NavLink>
              </li>
              <li data-aos="fade-up">
                <NavLink 
                  to="/careers" 
                  className={({ isActive }) => `${styles.pageScroll} ${isActive ? styles.active : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Careers
                </NavLink>
              </li>
              <li data-aos="fade-up">
                <NavLink 
                  to="/blogs" 
                  className={({ isActive }) => `${styles.pageScroll} ${isActive ? styles.active : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Blogs
                </NavLink>
              </li>
              <li data-aos="fade-up">
                <NavLink 
                  to="/contact" 
                  className={({ isActive }) => `${styles.pageScroll} ${isActive ? styles.active : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Contact Us
                </NavLink>
              </li>
            </ul>
          </div>
        )}
      </div>


      {/* Mobile View Overlay */}
      {isMobileView && isMobileMenuOpen && <div style={mobileOverlayStyles}></div>}

      {/* Mobile View Navigation (Above the grey overlay) */}
      {isMobileView && isMobileMenuOpen && (
        <div style={mobileDropdownStyles} data-aos="fade-down" data-aos-duration="300" ref={mobileDropdownRef}>
          <button
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: "absolute",
              top: "10px",
              right: "40px",
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "28px",
              cursor: "pointer",
              zIndex: 1001
            }}
            aria-label="Close menu"
          >
            ✕
          </button>
          <NavLink 
            to="/about" 
            className={({ isActive }) => `${styles.dropdownLink} ${isActive ? styles.activeDropdown : ''}`}
            onClick={handleNavLinkClick}
          >
            About Us
          </NavLink>
          <NavLink 
            to="/portfolio" 
            className={({ isActive }) => `${styles.dropdownLink} ${isActive ? styles.activeDropdown : ''}`}
            onClick={handleNavLinkClick}
          >
            Portfolio
          </NavLink>
          <div className={styles.mobileDropdownGroup}>
            <span 
              className={`${styles.mobileDropdownTitle} ${styles.expertiseDropdownTitle}`}
              onClick={() => setExpertiseDropdownOpen(!expertiseDropdownOpen)}
            >
              Expertise <i id="mobileExpertiseArrow" className={`fa fa-chevron-${expertiseDropdownOpen ? 'up' : 'down'} ${styles.customMobileArrow}`} style={{fontSize: '8px', marginLeft: '8px'}}></i>
            </span>
            {expertiseDropdownOpen && (
              <div className={`${styles.mobileSubMenu} ${styles.show}`}>
                <NavLink 
                  to="/ourtech" 
                  className={({ isActive }) => `${styles.dropdownLink} ${styles.subLink} ${isActive ? styles.activeDropdown : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Our Tech
                </NavLink>
                <NavLink 
                  to="/services" 
                  className={({ isActive }) => `${styles.dropdownLink} ${styles.subLink} ${isActive ? styles.activeDropdown : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Our Services
                </NavLink>
                <NavLink 
                  to="/our-products" 
                  className={({ isActive }) => `${styles.dropdownLink} ${styles.subLink} ${isActive ? styles.activeDropdown : ''}`}
                  onClick={handleNavLinkClick}
                >
                  Our Products
                </NavLink>
              </div>
            )}
          </div>
          <NavLink 
            to="/teams" 
            className={({ isActive }) => `${styles.dropdownLink} ${isActive ? styles.activeDropdown : ''}`}
            onClick={handleNavLinkClick}
          >
            Our Team
          </NavLink>
          <NavLink 
            to="/careers" 
            className={({ isActive }) => `${styles.dropdownLink} ${isActive ? styles.activeDropdown : ''}`}
            onClick={handleNavLinkClick}
          >
            Careers
          </NavLink>
          <NavLink 
            to="/blogs" 
            className={({ isActive }) => `${styles.dropdownLink} ${isActive ? styles.activeDropdown : ''}`}
            onClick={handleNavLinkClick}
          >
            Blogs
          </NavLink>
          <NavLink 
            to="/contact" 
            className={({ isActive }) => `${styles.dropdownLink} ${isActive ? styles.activeDropdown : ''}`}
            onClick={handleNavLinkClick}
          >
            Contact Us
          </NavLink>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
